/**
 * BrandedTransition — THE standard "something is happening" surface for the
 * trial funnel (2026-08-09 owner directive: after every submission it must be
 * abundantly clear the process is moving along; never a tiny unbranded line,
 * never a raw URL).
 *
 * Composition: prest0 wordmark + brand spinner + a clear lowercase status
 * line (house style) + optional subtitle. Contract: copy props ONLY — this
 * component never receives or renders a URL, path, or code.
 *
 * Two variants:
 *   - "card": fits inside an existing panel/login card (compact paddings).
 *   - "page": full-viewport centered takeover (route fallbacks, bootstraps).
 *
 * Deliberately dependency-light (no context hooks, no router) so it is safe
 * inside route-level Suspense fallbacks.
 */

import React from 'react';
import styled from 'styled-components';
import BrandWordmark from '../BrandWordmark';
import Spinner from './Spinner';

const PageWrap = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.1rem;
  background: var(--brand-canvas);
  padding: 2rem 1.25rem;
  text-align: center;
`;

const CardWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.9rem;
  padding: 2.25rem 1.25rem;
  text-align: center;
`;

const Title = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--brand-black);
  letter-spacing: -0.01em;
  /* progressive enhancement — older Safari ignores it harmlessly */
  text-wrap: balance;
`;

const Subtitle = styled.div`
  font-size: 0.85rem;
  color: var(--brand-text-muted);
  max-width: 34ch;
  line-height: 1.45;
  text-wrap: pretty;
`;

export interface BrandedTransitionProps {
  /** Clear lowercase status line, e.g. "signing you in…". Never a URL. */
  title: string;
  /** Optional supporting line, e.g. "your invite code applies next". */
  subtitle?: string;
  variant?: 'card' | 'page';
  className?: string;
}

export const BrandedTransition: React.FC<BrandedTransitionProps> = ({
  title,
  subtitle,
  variant = 'card',
  className,
}) => {
  const Wrap = variant === 'page' ? PageWrap : CardWrap;
  return (
    <Wrap className={className} role="status" aria-live="polite">
      <BrandWordmark />
      <Spinner />
      <Title>{title}</Title>
      {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
    </Wrap>
  );
};

export default BrandedTransition;
